import { unwrap } from "@sagot/api-client";
import { CONSOLE_PATHS, PASSWORD_MAX_LENGTH, PRODUCT_NAME } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { AppLink, Button, EmptyState, Icon, Skeleton, toast } from "@sagot/ui";
import { useQuery } from "@tanstack/react-query";
import { useState, type SyntheticEvent } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { useErrorMessage } from "../lib/api-errors";
import { allRulesPass, passwordRules } from "../lib/password";
import { queryKeys } from "../lib/query-keys";
import { useApi } from "../session/session-provider";
import { AuthLayout } from "./components/auth-layout";
import { FormAlert } from "./components/form-alert";
import { IconBadge } from "./components/icon-badge";
import { PasswordInput } from "./components/password-input";
import { PasswordRulesList } from "./components/password-rules";
import { PasswordStrength } from "./components/password-strength";

const TOKEN_MIN_LENGTH = 16;

function InvalidLink() {
  const { t } = useTranslation();
  return (
    <div className="card">
      <h1 className="sr-only">{t("auth.reset.invalidTitle")}</h1>
      <EmptyState
        icon="alert-triangle"
        title={t("auth.reset.invalidTitle")}
        description={t("auth.reset.invalidBody")}
        action={
          <Button asChild>
            <AppLink href={CONSOLE_PATHS.forgotPassword}>{t("auth.reset.requestNew")}</AppLink>
          </Button>
        }
      />
    </div>
  );
}

function ResetForm({ token, email }: { token: string; email: string }) {
  const { t } = useTranslation();
  const api = useApi();
  const navigate = useNavigate();
  const message = useErrorMessage();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const rules = passwordRules(password, confirm);
  const ready = allRulesPass(rules) && password.length <= PASSWORD_MAX_LENGTH;

  const onSubmit = async (event: SyntheticEvent) => {
    event.preventDefault();
    if (!ready) return;
    setSubmitting(true);
    setError(null);
    try {
      await unwrap(api.POST("/v1/auth/reset", { body: { token, password } }));
      toast.success(t("auth.reset.done"));
      await navigate(CONSOLE_PATHS.login, { replace: true });
    } catch (caught) {
      setError(message(caught));
      setSubmitting(false);
    }
  };

  return (
    <>
      <IconBadge icon="lock" />
      <h1 className="mt-5 text-2xl font-extrabold tracking-tight">{t("auth.reset.heading")}</h1>
      <p className="mt-1 text-fg-muted">
        {t("auth.reset.forLead")} <strong className="text-fg">{email}</strong>. {t("auth.reset.forTail")}
      </p>
      {error ? <FormAlert title={error} onDismiss={() => { setError(null); }} className="mt-5" /> : null}
      <form className="mt-6 space-y-4" noValidate onSubmit={(event) => void onSubmit(event)}>
        <div>
          <label className="label" htmlFor="rp-pw">
            {t("auth.reset.newPassword")}
          </label>
          {/* eslint-disable-next-line jsx-a11y/no-autofocus -- the new password is the page's only task */}
          <PasswordInput id="rp-pw" placeholder={t("auth.password.placeholder")} autoComplete="new-password" autoFocus value={password} onChange={(e) => { setPassword(e.target.value); }} />
          <PasswordStrength value={password} />
        </div>
        <div>
          <label className="label" htmlFor="rp-confirm">
            {t("auth.reset.confirm")}
          </label>
          <PasswordInput id="rp-confirm" autoComplete="new-password" value={confirm} onChange={(e) => { setConfirm(e.target.value); }} />
        </div>
        <PasswordRulesList rules={rules} />
        <Button type="submit" size="lg" className="w-full" loading={submitting} disabled={submitting || !ready}>
          {t("auth.reset.submit")}
        </Button>
      </form>
      <p className="mt-6 text-center text-sm text-fg-muted">
        <AppLink href={CONSOLE_PATHS.login} className="font-semibold text-primary-ink hover:underline">
          {t("auth.common.backToSignIn")}
        </AppLink>
      </p>
    </>
  );
}

export function ResetPasswordPage() {
  const { t } = useTranslation();
  const api = useApi();
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const valid = token.length >= TOKEN_MIN_LENGTH;
  const validation = useQuery({
    queryKey: queryKeys.resetToken(token),
    queryFn: () => unwrap(api.GET("/v1/auth/reset/validate", { params: { query: { token } } })),
    enabled: valid,
    retry: false,
    staleTime: Infinity,
  });

  let content;
  if (!valid || validation.isError) content = <InvalidLink />;
  else if (validation.isPending)
    content = (
      <div className="space-y-4" aria-busy="true">
        <Skeleton className="size-12 rounded-lg" />
        <Skeleton className="h-8 w-[220px]" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    );
  else content = <ResetForm token={token} email={validation.data.email} />;

  return (
    <AuthLayout title={t("auth.reset.title", { product: PRODUCT_NAME })} back={{ label: t("auth.common.backToSignIn"), href: CONSOLE_PATHS.login }}>
      {content}
      {!valid || validation.isError ? (
        <p className="mt-6 text-center text-sm text-fg-muted">
          <AppLink href={CONSOLE_PATHS.login} className="inline-flex items-center gap-1 font-semibold text-primary-ink hover:underline">
            <Icon name="arrow-left" className="size-3.5" />
            {t("auth.common.backToSignIn")}
          </AppLink>
        </p>
      ) : null}
    </AuthLayout>
  );
}
